import { useEffect, useState } from 'react'
import { api, AdminStats, AdminUserRow, AdminUserDetail } from '../lib/api'
import { Spinner } from '../components/ui'
import {
  Users, MessageSquare, FolderKanban, TrendingUp,
  Search, ChevronDown, ChevronUp, Shield, Trash2,
  RefreshCw, X, Crown
} from 'lucide-react'

const PLAN_COLORS: Record<'free' | 'indie' | 'studio', { bg: string; color: string; label: string }> = {
  free:   { bg: 'rgba(113,117,135,0.12)', color: '#555770', label: 'Free' },
  indie:  { bg: 'rgba(53,37,205,0.1)',    color: '#3525cd', label: 'Indie' },
  studio: { bg: 'rgba(220,120,0,0.12)',   color: '#b06000', label: 'Studio' },
}

const TAG_COLORS: Record<string, string> = {
  bug: '#ef4444', feature: '#3525cd', praise: '#22c55e',
  performance: '#f59e0b', other: '#8b5cf6',
}

function StatCard({ icon: Icon, label, value, sub, color = '#3525cd' }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>
  label: string; value: string | number; sub?: string; color?: string
}) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 24px',
      boxShadow: '0 1px 4px rgba(25,28,30,0.07)', flex: 1, minWidth: 160,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={17} color={color} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#777587', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#191c1e', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#777587', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function PlanBadge({ plan }: { plan: string }) {
  const s = PLAN_COLORS[(plan as 'free' | 'indie' | 'studio')] ?? PLAN_COLORS.free
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color,
    }}>
      {plan === 'studio' && <Crown size={10} />}
      {s.label}
    </span>
  )
}

function UserDetailModal({ userId, onClose, onPlanChange, onDelete }: {
  userId: string
  onClose: () => void
  onPlanChange: (id: string, plan: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}) {
  const [data, setData] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [planChanging, setPlanChanging] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    api.admin.getUser(userId).then(setData).finally(() => setLoading(false))
  }, [userId])

  async function handlePlanSave() {
    if (!data || !selectedPlan) return
    setPlanChanging(true)
    await onPlanChange(userId, selectedPlan)
    setData(d => d ? { ...d, user: { ...d.user, plan: selectedPlan } } : d)
    setSelectedPlan(null)
    setPlanChanging(false)
  }

  async function handleDelete() {
    setDeleting(true)
    await onDelete(userId)
    onClose()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 18, width: 680, maxWidth: '95vw', maxHeight: '85vh',
        overflow: 'auto', padding: 32, position: 'relative',
        boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'none', border: 'none', cursor: 'pointer', color: '#777587', padding: 4,
        }}>
          <X size={18} />
        </button>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
        ) : !data ? (
          <div style={{ color: '#ef4444', textAlign: 'center', padding: 40 }}>Failed to load user</div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: 'rgba(53,37,205,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 800, color: '#3525cd', flexShrink: 0,
              }}>
                {data.user.name?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#191c1e' }}>{data.user.name}</div>
                <div style={{ fontSize: 13, color: '#777587' }}>{data.user.email}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <PlanBadge plan={data.user.plan} />
                  <span style={{ fontSize: 11, color: '#aaa' }}>
                    Joined {new Date(data.user.created_at).toLocaleDateString()}
                  </span>
                  {data.user.dodo_subscription_id && (
                    <span style={{ fontSize: 11, color: '#22c55e', background: 'rgba(34,197,94,0.1)', padding: '2px 7px', borderRadius: 999 }}>
                      Active subscription
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Projects', value: data.user.project_count },
                { label: 'Total Submissions', value: data.user.total_submissions },
                { label: 'This Month', value: data.user.monthly_submissions },
              ].map(s => (
                <div key={s.label} style={{ background: '#f7f9fb', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#191c1e' }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#777587', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Change plan */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#464555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Change Plan</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['free', 'indie', 'studio'].map(p => {
                  const isCurrent = data.user.plan === p && !selectedPlan
                  const isSelected = (selectedPlan ?? data.user.plan) === p
                  return (
                    <button key={p} disabled={planChanging} onClick={() => setSelectedPlan(p === data.user.plan ? null : p)} style={{
                      flex: 1, padding: '7px 0', borderRadius: 8, border: `2px solid ${isSelected ? '#3525cd' : '#e5e7eb'}`,
                      background: isSelected ? 'rgba(53,37,205,0.07)' : '#fff',
                      color: isSelected ? '#3525cd' : '#464555',
                      fontWeight: 700, fontSize: 13, cursor: planChanging ? 'default' : 'pointer',
                      opacity: planChanging ? 0.5 : 1,
                    }}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                      {isCurrent && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.6 }}>current</span>}
                    </button>
                  )
                })}
              </div>
              {selectedPlan && selectedPlan !== data.user.plan && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={handlePlanSave} disabled={planChanging} style={{
                    padding: '8px 20px', background: '#3525cd', border: 'none',
                    borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    opacity: planChanging ? 0.6 : 1,
                  }}>
                    {planChanging ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setSelectedPlan(null)} disabled={planChanging} style={{
                    padding: '8px 14px', background: '#f0f0f0', border: 'none',
                    borderRadius: 8, color: '#464555', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Projects */}
            {data.projects.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#464555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Projects ({data.projects.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.projects.map(p => (
                    <div key={p.id} style={{ background: '#f7f9fb', borderRadius: 9, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#191c1e' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#777587' }}>{p.platform} · {p.environment}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#464555', textAlign: 'right' }}>
                        <div><b>{p.feedback_count}</b> total</div>
                        <div style={{ color: '#ef4444' }}>{p.open_count} open</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent feedback */}
            {data.recentFeedback.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#464555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Recent Submissions
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.recentFeedback.slice(0, 8).map(f => (
                    <div key={f.id} style={{ background: '#f7f9fb', borderRadius: 9, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#777587' }}>{f.project_name}</span>
                        {f.tag && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: `${TAG_COLORS[f.tag]}18`, color: TAG_COLORS[f.tag] }}>
                            {f.tag}
                          </span>
                        )}
                        <span style={{ fontSize: 10, color: f.status === 'open' ? '#ef4444' : '#22c55e', marginLeft: 'auto' }}>{f.status}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#191c1e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Danger zone */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Danger Zone
              </div>
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 8, color: '#ef4444', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}>
                  <Trash2 size={14} /> Delete User &amp; All Data
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#ef4444' }}>Are you sure? This is permanent.</span>
                  <button onClick={handleDelete} disabled={deleting} style={{
                    padding: '7px 14px', background: '#ef4444', border: 'none',
                    borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}>
                    {deleting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} style={{
                    padding: '7px 14px', background: '#f0f0f0', border: 'none',
                    borderRadius: 8, color: '#464555', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function Admin() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [users, setUsers] = useState<AdminUserRow[]>([])
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 })
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [usersLoading, setUsersLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [sortAsc, setSortAsc] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    api.admin.stats()
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    setUsersLoading(true)
    api.admin.users({ search: search || undefined, plan: planFilter || undefined, page: String(page) })
      .then(r => { setUsers(r.users); setPagination({ page: r.pagination.page, total: r.pagination.total, pages: r.pagination.pages }) })
      .catch(e => setError(e.message))
      .finally(() => setUsersLoading(false))
  }, [search, planFilter, page])

  async function handlePlanChange(id: string, plan: string) {
    await api.admin.updatePlan(id, plan)
    setUsers(us => us.map(u => u.id === id ? { ...u, plan } : u))
  }

  async function handleDelete(id: string) {
    await api.admin.deleteUser(id)
    setUsers(us => us.filter(u => u.id !== id))
    setSelectedUserId(null)
  }

  const sortedUsers = [...users].sort((a, b) =>
    sortAsc ? a.created_at - b.created_at : b.created_at - a.created_at
  )

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>
  )

  if (error) return (
    <div style={{ padding: 40, color: '#ef4444', textAlign: 'center' }}>
      <Shield size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 600 }}>{error}</div>
      <div style={{ fontSize: 13, color: '#777587', marginTop: 4 }}>
        Make sure ADMIN_EMAIL is set in your Worker env and matches your account.
      </div>
    </div>
  )

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(53,37,205,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Shield size={20} color="#3525cd" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#191c1e' }}>God Mode</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#777587' }}>Platform-wide admin overview</p>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard icon={Users} label="Total Users" value={stats.users.total}
              sub={`${stats.users.indie_count} indie · ${stats.users.studio_count} studio`} />
            <StatCard icon={FolderKanban} label="Projects" value={stats.projects.total} color="#8b5cf6" />
            <StatCard icon={MessageSquare} label="All Submissions" value={stats.feedback.total}
              sub={`${stats.feedback.this_month} this month`} color="#22c55e" />
            <StatCard icon={TrendingUp} label="Today" value={stats.feedback.today}
              sub={`${stats.feedback.this_week} this week`} color="#f59e0b" />
          </div>

          {/* Plan breakdown */}
          <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(25,28,30,0.07)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#464555', marginBottom: 14 }}>Plan Distribution</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { plan: 'free', count: stats.users.free_count, pct: stats.users.total ? Math.round(stats.users.free_count / stats.users.total * 100) : 0 },
                { plan: 'indie', count: stats.users.indie_count, pct: stats.users.total ? Math.round(stats.users.indie_count / stats.users.total * 100) : 0 },
                { plan: 'studio', count: stats.users.studio_count, pct: stats.users.total ? Math.round(stats.users.studio_count / stats.users.total * 100) : 0 },
              ].map(({ plan, count, pct }) => {
                const s = PLAN_COLORS[plan as 'free' | 'indie' | 'studio']
                return (
                  <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#191c1e' }}>{s.label}</span>
                    <span style={{ fontSize: 13, color: '#777587' }}>{count} users ({pct}%)</span>
                  </div>
                )
              })}
            </div>
            {/* Bar */}
            <div style={{ marginTop: 12, height: 8, borderRadius: 4, background: '#f0f0f0', overflow: 'hidden', display: 'flex' }}>
              {[
                { plan: 'free', count: stats.users.free_count },
                { plan: 'indie', count: stats.users.indie_count },
                { plan: 'studio', count: stats.users.studio_count },
              ].map(({ plan, count }) => {
                const pct = stats.users.total ? (count / stats.users.total * 100) : 0
                return (
                  <div key={plan} style={{ width: `${pct}%`, background: PLAN_COLORS[plan as 'free' | 'indie' | 'studio'].color, transition: 'width 0.4s' }} />
                )
              })}
            </div>
          </div>

          {/* Tags breakdown */}
          {stats.tags.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(25,28,30,0.07)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#464555', marginBottom: 14 }}>Submissions by Tag</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {stats.tags.map(t => (
                  <div key={t.tag} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, background: `${TAG_COLORS[t.tag] ?? '#8b5cf6'}18` }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: TAG_COLORS[t.tag] ?? '#8b5cf6' }}>{t.tag}</span>
                    <span style={{ fontSize: 13, color: '#464555' }}>{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Users table */}
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(25,28,30,0.07)', overflow: 'hidden' }}>
        {/* Table header / filters */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#191c1e', flex: 1 }}>
            All Users <span style={{ fontSize: 13, color: '#777587', fontWeight: 500 }}>({pagination.total})</span>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#777587' }} />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search email or name..."
              style={{ paddingLeft: 28, paddingRight: 12, paddingTop: 7, paddingBottom: 7, background: '#f2f4f6', border: 'none', borderRadius: 8, fontSize: 13, color: '#191c1e', outline: 'none', width: 220 }}
            />
          </div>

          {/* Plan filter */}
          <select
            value={planFilter}
            onChange={e => { setPlanFilter(e.target.value); setPage(1) }}
            style={{ padding: '7px 28px 7px 10px', background: '#f2f4f6', border: 'none', borderRadius: 8, fontSize: 13, color: '#191c1e', outline: 'none', appearance: 'none', cursor: 'pointer' }}
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="indie">Indie</option>
            <option value="studio">Studio</option>
          </select>

          <button onClick={() => { setSearch(''); setPlanFilter(''); setPage(1) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#777587', padding: 6, borderRadius: 6 }} title="Reset filters">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                {[
                  { key: 'user', label: 'User', width: '30%' },
                  { key: 'plan', label: 'Plan', width: '10%' },
                  { key: 'projects', label: 'Projects', width: '10%' },
                  { key: 'submissions', label: 'Total / Month', width: '16%' },
                  { key: 'joined', label: 'Joined', sortable: true, width: '14%' },
                  { key: 'actions', label: '', width: '10%' },
                ].map(col => (
                  <th key={col.key} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                    color: '#777587', textTransform: 'uppercase', letterSpacing: '0.05em',
                    width: col.width, cursor: col.sortable ? 'pointer' : 'default',
                  }}
                    onClick={col.sortable ? () => setSortAsc(v => !v) : undefined}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {col.label}
                      {col.sortable && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center' }}><Spinner /></td></tr>
              ) : sortedUsers.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#777587', fontSize: 14 }}>No users found</td></tr>
              ) : (
                sortedUsers.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f7f7f7', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(53,37,205,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#3525cd', flexShrink: 0 }}>
                          {u.name?.[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#191c1e' }}>{u.name}</div>
                          <div style={{ fontSize: 12, color: '#777587' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}><PlanBadge plan={u.plan} /></td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#464555', fontWeight: 600 }}>{u.project_count}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#464555' }}>
                      <span style={{ fontWeight: 600 }}>{u.total_submissions}</span>
                      <span style={{ color: '#aaa' }}> / </span>
                      <span style={{ color: '#3525cd', fontWeight: 600 }}>{u.monthly_submissions}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#777587' }}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => setSelectedUserId(u.id)} style={{
                        padding: '5px 12px', background: 'rgba(53,37,205,0.07)',
                        border: 'none', borderRadius: 7, color: '#3525cd',
                        fontWeight: 600, fontSize: 12, cursor: 'pointer',
                      }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: '#777587' }}>
              Page {pagination.page} of {pagination.pages} ({pagination.total} users)
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page <= 1 ? 'default' : 'pointer', color: page <= 1 ? '#bbb' : '#464555', fontWeight: 600, fontSize: 13 }}>← Prev</button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page >= pagination.pages ? 'default' : 'pointer', color: page >= pagination.pages ? '#bbb' : '#464555', fontWeight: 600, fontSize: 13 }}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* User detail modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onPlanChange={handlePlanChange}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}
